import { useCallback, useEffect, useRef, useState } from 'react';
import { navigate } from '../routing';
import { loadReport, newReportId, saveReport } from './reportCache';
import { TbcSessionSnapshot } from './types';
import { useTbcAnalyzerSession } from './useTbcAnalyzerSession';

type TbcAnalyzerSession = ReturnType<typeof useTbcAnalyzerSession>;

/** Props App passes to every routed TBC analyzer. */
export interface TbcReportRouteProps {
    basePath: string;
    reportId: string | null;
}

export interface RegeneratedReport<TResult, TExtra> {
    result: TResult;
    snapshot: TbcSessionSnapshot;
    extra?: TExtra;
}

interface UseTbcReportRouteOptions<TResult, TExtra> {
    /** Tab path, e.g. `/consumes`. Reports live at `${basePath}/report/<id>`. */
    basePath: string;
    /** Report id from the current URL, or null on the tab's setup screen. */
    reportId: string | null;
    session: TbcAnalyzerSession;
    /** Put a cached report back on screen (session state is restored by the hook). */
    onRestore: (result: TResult, extra: TExtra | undefined) => void;
    /** Clear analyzer-local result state when the URL leaves a report. */
    onLeave: () => void;
    /**
     * For analyzers whose report id encodes the request: rebuild the report on a cache miss.
     * Resolve null on failure, with the session error already set.
     */
    regenerate?: (reportId: string) => Promise<RegeneratedReport<TResult, TExtra> | null>;
}

const EXPIRED_REPORT_ERROR =
    'That report is no longer cached in this browser. Load the logs and run the analysis again.';

/**
 * Ties an analyzer's results phase to the URL. A finished analysis is cached in the browser and
 * pushed as `${basePath}/report/<id>`; back/forward and reloads restore it while the cache holds it,
 * and analyzers with `regenerate` rebuild it from the id when it is not cached.
 */
export function useTbcReportRoute<TResult, TExtra = undefined>(options: UseTbcReportRouteOptions<TResult, TExtra>) {
    const { basePath, reportId } = options;
    const latest = useRef(options);
    latest.current = options;
    const shownIdRef = useRef<string | null>(null);
    const [restoring, setRestoring] = useState(reportId !== null);
    const [regenerating, setRegenerating] = useState(false);

    useEffect(() => {
        if (reportId === shownIdRef.current) {
            setRestoring(false);
            setRegenerating(false);
            return;
        }
        if (reportId === null) {
            const { session, onLeave } = latest.current;
            shownIdRef.current = null;
            setRestoring(false);
            setRegenerating(false);
            if (session.phase === 'results') {
                session.handleBackToSelection();
                onLeave();
            }
            return;
        }

        let cancelled = false;
        const show = (entry: RegeneratedReport<TResult, TExtra>) => {
            shownIdRef.current = reportId;
            latest.current.session.restoreSnapshot(entry.snapshot);
            latest.current.onRestore(entry.result, entry.extra);
        };
        const fail = (error: string | null) => {
            const { session, onLeave } = latest.current;
            shownIdRef.current = null;
            if (session.phase === 'results') {
                session.setPhase('selection');
                onLeave();
            }
            if (error) {
                session.setError(error);
            }
            navigate(basePath, { replace: true });
        };

        setRestoring(true);
        void (async () => {
            const cached = await loadReport(reportId);
            if (cancelled) {
                return;
            }
            if (cached && cached.basePath === basePath) {
                setRestoring(false);
                show({ result: cached.result as TResult, snapshot: cached.session, extra: cached.extra as TExtra });
                return;
            }
            const { regenerate } = latest.current;
            if (!regenerate) {
                setRestoring(false);
                fail(EXPIRED_REPORT_ERROR);
                return;
            }
            setRegenerating(true);
            const rebuilt = await regenerate(reportId);
            if (cancelled) {
                return;
            }
            setRestoring(false);
            setRegenerating(false);
            if (!rebuilt) {
                fail(null);
                return;
            }
            void saveReport({
                id: reportId,
                basePath,
                savedAt: Date.now(),
                session: rebuilt.snapshot,
                extra: rebuilt.extra,
                result: rebuilt.result,
            });
            show(rebuilt);
        })();
        return () => {
            cancelled = true;
        };
    }, [basePath, reportId]);

    /**
     * Cache a fresh result and move the URL onto it. Call after the results phase is on screen.
     * Pass `id` when the analyzer encodes its request as the report id (shareable links).
     */
    const publish = useCallback(
        (result: TResult, extra?: TExtra, id: string = newReportId()) => {
            shownIdRef.current = id;
            void saveReport({
                id,
                basePath,
                savedAt: Date.now(),
                session: latest.current.session.snapshot(),
                extra,
                result,
            });
            navigate(`${basePath}/report/${id}`);
        },
        [basePath]
    );

    /** Back to the selection screen; the URL change drives the state change. */
    const leave = useCallback(() => {
        navigate(basePath);
    }, [basePath]);

    return { restoring, regenerating, publish, leave };
}
